/**
 * Em volta das telas carregadas sob demanda (`React.lazy`). Sem ele, a falha
 * ao baixar o chunk de uma tela (ver `chunkLoadError.ts`) desmonta o app
 * inteiro e a página fica em branco até a pessoa atualizar. Com ele, o
 * painel recarrega sozinho uma vez; se acabou de recarregar, mostra o aviso
 * com o botão de atualizar. Qualquer outro erro passa adiante, como antes.
 *
 * `resetKey` (o caminho) tenta a tela de novo quando a pessoa navega.
 */
import {Component} from 'react';

import {isChunkLoadError, reloadOnceAfterChunkError} from './chunkLoadError';

type Props = {
  resetKey?: string;
};

type State = {
  error: Error | null;
  reloading: boolean;
};

class ChunkErrorBoundary extends Component<Props, State> {
  state: State = {error: null, reloading: false};

  static getDerivedStateFromError(error: Error): Partial<State> {
    return {error};
  }

  componentDidCatch(error: Error) {
    if (isChunkLoadError(error)) {
      this.setState({reloading: reloadOnceAfterChunkError()});
    }
  }

  componentDidUpdate(previous: Props) {
    if (this.state.error && previous.resetKey !== this.props.resetKey) {
      this.setState({error: null, reloading: false});
    }
  }

  render() {
    const {error, reloading} = this.state;
    if (!error) return this.props.children;
    if (!isChunkLoadError(error)) throw error;
    if (reloading) return null;
    return (
      <div className='card'>
        <div className='card-body py-15 text-center'>
          <h3 className='fs-4 fw-bolder text-gray-800 mb-2'>
            Não foi possível abrir esta tela
          </h3>
          <p className='text-muted mb-6'>
            O painel foi atualizado ou a conexão caiu. Atualize a página para
            continuar.
          </p>
          <button
            type='button'
            className='btn btn-sm btn-primary'
            onClick={() => window.location.reload()}
          >
            Atualizar página
          </button>
        </div>
      </div>
    );
  }
}

export {ChunkErrorBoundary};
